"use client";

import React from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Generic Select Input Component for reference data
export interface SelectOption<Value extends string = string> {
  value: Value;
  label: string;
  disabled?: boolean;
}

interface SelectInputProps<Value extends string = string> extends Omit<
  React.ComponentProps<typeof SelectTrigger>,
  "value" | "onValueChange"
> {
  label?: string;
  value: Value;
  options: SelectOption<Value>[];
  placeholder?: string;
  emptyMessage?: string;
  onValueChange: (value: Value) => void;
}

/**
 * Option-list select. With `label`, renders the shared `Select` field chrome; the
 * trigger shows the selected option label.
 */
export function SelectInput<Value extends string = string>({
  label,
  value,
  options,
  placeholder = "Selecione uma opção",
  emptyMessage = "Nenhuma opção disponível",
  onValueChange,
  className,
  id: idProp,
  ...props
}: SelectInputProps<Value>) {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const selectedOption = options.find((option) => option.value === value);

  return (
    <Select<Value>
      id={id}
      label={label}
      name={id}
      onValueChange={(nextValue) => nextValue !== null && onValueChange(nextValue)}
      value={value}
    >
      <SelectTrigger {...props} className={className}>
        <SelectValue placeholder={placeholder}>
          {selectedOption ? selectedOption.label : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.length === 0 ? (
          <div className="text-muted-foreground px-2 py-1.5">{emptyMessage}</div>
        ) : (
          options.map((option) => (
            <SelectItem disabled={option.disabled} key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}

// Yes/No Select Component for boolean options
interface YesNoSelectProps extends Omit<
  React.ComponentProps<typeof SelectTrigger>,
  "value" | "onValueChange"
> {
  label?: string;
  value: string;
  onValueChange: (value: boolean) => void;
  placeholder?: string;
  yesLabel?: string;
  noLabel?: string;
}

export function YesNoSelect({
  value,
  onValueChange,
  placeholder = "Selecione uma opção",
  yesLabel = "Sim",
  noLabel = "Não",
  className,
  ...props
}: YesNoSelectProps) {
  const handleValueChange = (selectValue: string) => {
    const boolValue = selectValue === "yes";
    onValueChange(boolValue);
  };

  const options: SelectOption<"yes" | "no">[] = [
    { value: "yes", label: yesLabel },
    { value: "no", label: noLabel },
  ];
  const selectValue = value === "yes" || value === "no" ? value : "no";

  return (
    <SelectInput
      {...props}
      className={className}
      onValueChange={handleValueChange}
      options={options}
      placeholder={placeholder}
      value={selectValue}
    />
  );
}

// Utility functions for option formatting
export const createSelectOption = <Value extends string>(
  value: Value,
  label: string,
  disabled?: boolean,
): SelectOption<Value> => ({
  value,
  label,
  disabled,
});

// Reference data specific option creators
export const createSpeciesOptions = (species: { id: string; title: string }[]): SelectOption[] =>
  species.map((specie) => createSelectOption(specie.id, specie.title));

export const createDescriptionOptions = (
  descriptions: { id: string; title: string }[],
): SelectOption[] => descriptions.map((desc) => createSelectOption(desc.id, desc.title));

export const createAgeOptions = (ages: { id: string; title: string }[]): SelectOption[] =>
  ages.map((age) => createSelectOption(age.id, age.title));
